import type { ValueOf } from 'type-fest'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import type { DynamicConstants } from 'src/services/constants'
import { getRatioOfTwoNumbers } from 'src/utils/numerical'

export const samplingMethodChoices = [
  'Euler a',
  'Euler',
  'LMS',
  'Heun',
  'DPM2 Karras',
  'DPM2 a Karras',
  'DDIM',
  'DPM++ 2M Karras',
  'DPM++ SDE Karras',
]

export type SizeInfo = {
  width: number
  height: number
  extraProps?: {
    memberOnly?: boolean
    modelType?: GenerationModelType
    label?: string
  }
}
type Size = [number, number]
export const PRESET_SIZES_SDV1 = [
  [512, 1024],
  [768, 512],
  [768, 768],
  [768, 1024],
  [1024, 1024],
  [1280, 960],
  [960, 1280],
].sort((a, b) => a[0] - b[0] || a[1] - b[1]) as Size[]
export const PRESET_SIZES_SDXL = [
  [768, 1280],
  [1024, 1024],
  [896, 1152],
  [1280, 768],
].sort((a, b) => a[0] - b[0] || a[1] - b[1]) as Size[]
// Currently same as SDXL
export const PRESET_SIZES_DITV1 = PRESET_SIZES_SDXL
export const PRESET_SIZES_SDV1_FOR_MEMBER = [
  [1472, 832],
  [1344, 896],
  [1400, 1400],
  [896, 1344],
  [832, 1472],
] as Size[]
// Currently same as SD
export const PRESET_SIZES_SDXL_FOR_MEMBER = PRESET_SIZES_SDV1_FOR_MEMBER
export const PRESET_SIZES_DITV1_FOR_MEMBER = [
  [1472, 832],
  [1344, 896],
  [1392, 1392],
  [896, 1344],
  [832, 1472],
] as Size[]
export const PRESET_SIZES_MMDIT26A = PRESET_SIZES_SDXL
// Tsubaki.2 L size presets: longest edge = 1536 (dakimakura ratios like 1:3).
// These are ONLY reachable via preset selection, not manual input (capped at 1400).
export const PRESET_SIZES_MMDIT26A_FOR_MEMBER = [
  [1536, 864],
  [1536, 912],
  [1536, 1024],
  [1536, 1152],
  [1536, 1184],
  [1536, 1536],
  [1184, 1536],
  [1152, 1536],
  [1024, 1536],
  [912, 1536],
  [864, 1536],
  [512, 1536],
  [1536, 512],
] as Size[]

/**
 * Aspect ratios for the new size selector
 * Each ratio has a label and normalized width/height values
 */
export type AspectRatio = {
  label: string
  width: number
  height: number
  availableAt?: SizeTier[]
}

/**
 * Base aspect ratios (portrait orientation, except 1:1)
 * Use flipAspectRatio() to get landscape orientation
 */
// Tsubaki.2 extra ratios: 1:3 dakimakura ratio, only available in L size tier.
export const EXTRA_ASPECT_RATIOS_MMDIT26A: AspectRatio[] = [
  { label: '1:3', width: 1, height: 3, availableAt: ['large'] },
]

export const ASPECT_RATIOS: AspectRatio[] = [
  { label: '9:16', width: 9, height: 16 }, // 0.56
  { label: '3:5', width: 3, height: 5 }, // 0.6
  { label: '2:3', width: 2, height: 3 }, // 0.67
  { label: '3:4', width: 3, height: 4 }, //0.75
  { label: '7:9', width: 7, height: 9 }, // 0.78
  { label: '1:1', width: 1, height: 1 }, // 1.0
]

export type Orientation = 'portrait' | 'landscape'

/**
 * Flip aspect ratio between portrait and landscape
 */
export function flipAspectRatio(ratio: AspectRatio): AspectRatio {
  if (ratio.width === ratio.height) return ratio // 1:1 doesn't flip
  return {
    label: `${ratio.height}:${ratio.width}`,
    width: ratio.height,
    height: ratio.width,
  }
}

/**
 * Get aspect ratio with orientation applied
 */
export function getOrientedAspectRatio(
  ratio: AspectRatio,
  orientation: Orientation,
): AspectRatio {
  const isPortrait = ratio.width < ratio.height
  const isSquare = ratio.width === ratio.height
  if (isSquare) return ratio
  if (orientation === 'portrait' && !isPortrait) return flipAspectRatio(ratio)
  if (orientation === 'landscape' && isPortrait) return flipAspectRatio(ratio)
  return ratio
}

/**
 * Size tiers for the new size selector
 * - medium: Non-member sizes
 * - large: Member sizes (max ~1400)
 */
export type SizeTier = 'medium' | 'large'

/**
 * Size tier configurations by model type
 * Each tier defines the longest edge for that tier
 */
export const SIZE_TIER_CONFIG = {
  [GenerationModelType.SdV1Model]: {
    medium: { longestEdge: 1024, memberOnly: false },
    large: { longestEdge: 1400, memberOnly: true },
  },
  [GenerationModelType.SdxlModel]: {
    medium: { longestEdge: 1280, squareEdge: 1024, memberOnly: false },
    large: { longestEdge: 1400, memberOnly: true },
  },
  [GenerationModelType.Dit7Model]: {
    medium: { longestEdge: 1280, squareEdge: 1024, memberOnly: false },
    large: { longestEdge: 1400, memberOnly: true },
  },
  // Tsubaki.2: L tier longest edge is 1536 (vs 1400 for other XL models)
  [GenerationModelType.Mmdit26AModel]: {
    medium: { longestEdge: 1280, squareEdge: 1024, memberOnly: false },
    large: { longestEdge: 1536, memberOnly: true },
  },
} as const

/**
 * Calculate width and height from aspect ratio and size tier
 */
export function calculateSizeFromRatioAndTier(
  ratio: AspectRatio,
  tier: SizeTier,
  modelType: GenerationModelType,
  divisor: number = 8,
): { width: number; height: number } {
  const config =
    SIZE_TIER_CONFIG[modelType as keyof typeof SIZE_TIER_CONFIG] ??
    SIZE_TIER_CONFIG[GenerationModelType.SdxlModel]
  const tierConfig = config[tier]
  // 1:1 at longestEdge 1280 produces 1280x1280, but intended default is 1024x1024
  const isSquare = ratio.width === ratio.height
  const longestEdge =
    (isSquare && 'squareEdge' in tierConfig
      ? tierConfig.squareEdge
      : undefined) ?? tierConfig.longestEdge

  let width: number
  let height: number

  if (ratio.width >= ratio.height) {
    // Landscape or square
    width = longestEdge
    height = Math.round((longestEdge * ratio.height) / ratio.width)
  } else {
    // Portrait
    height = longestEdge
    width = Math.round((longestEdge * ratio.width) / ratio.height)
  }

  // Ensure divisibility
  width = Math.floor(width / divisor) * divisor
  height = Math.floor(height / divisor) * divisor

  return { width, height }
}

export const RATIO_LABEL_REPLACEMENTS = { '23:13': '16:9', '13:23': '9:16' }
export const PRESET_SIZES: SizeInfo[] = [
  ...PRESET_SIZES_SDV1.map(it =>
    sizeMapFn(it, { modelType: GenerationModelType.SdV1Model }),
  ),
  ...PRESET_SIZES_SDV1_FOR_MEMBER.map(it =>
    sizeMapFn(it, {
      modelType: GenerationModelType.SdV1Model,
      memberOnly: true,
    }),
  ),
  ...PRESET_SIZES_SDXL.map(it =>
    sizeMapFn(it, { modelType: GenerationModelType.SdxlModel }),
  ),
  ...PRESET_SIZES_SDXL_FOR_MEMBER.map(it =>
    sizeMapFn(it, {
      modelType: GenerationModelType.SdxlModel,
      memberOnly: true,
    }),
  ),
  ...PRESET_SIZES_DITV1.map(it =>
    sizeMapFn(it, { modelType: GenerationModelType.Dit7Model }),
  ),
  ...PRESET_SIZES_DITV1_FOR_MEMBER.map(it =>
    sizeMapFn(it, {
      modelType: GenerationModelType.Dit7Model,
      memberOnly: true,
    }),
  ),
  ...PRESET_SIZES_MMDIT26A.map(it =>
    sizeMapFn(it, { modelType: GenerationModelType.Mmdit26AModel }),
  ),
  ...PRESET_SIZES_MMDIT26A_FOR_MEMBER.map(it =>
    sizeMapFn(it, {
      modelType: GenerationModelType.Mmdit26AModel,
      memberOnly: true,
    }),
  ),
]

const SIDEBAR_SIZES_SD = [
  [512, 768, { memberOnly: false }],
  [512, 512, { memberOnly: false }],
  [896, 1344, { memberOnly: true }],
] as [number, number, Record<string, any>][]
const SIDEBAR_SIZES_SDXL = [
  [768, 1280, { memberOnly: false }],
  [1024, 1024, { memberOnly: false }],
  [832, 1472, { memberOnly: true }],
] as [number, number, Record<string, any>][]
const SIDEBAR_SIZES_DITV1 = SIDEBAR_SIZES_SDXL
// Tsubaki.2 sidebar: 864×1536 replaces SDXL's 832×1472 to showcase L size
const SIDEBAR_SIZES_MMDIT26A = [
  [768, 1280, { memberOnly: false }],
  [1024, 1024, { memberOnly: false }],
  [864, 1536, { memberOnly: true }],
] as [number, number, Record<string, any>][]

export const SIDEBAR_SIZES: SizeInfo[] = [
  ...SIDEBAR_SIZES_SD.map(it =>
    sizeMapFn(it, { modelType: GenerationModelType.SdV1Model }),
  ),
  ...SIDEBAR_SIZES_SDXL.map(it =>
    sizeMapFn(it, { modelType: GenerationModelType.SdxlModel }),
  ),
  ...SIDEBAR_SIZES_DITV1.map(it =>
    sizeMapFn(it, { modelType: GenerationModelType.Dit7Model }),
  ),
  ...SIDEBAR_SIZES_MMDIT26A.map(it =>
    sizeMapFn(it, { modelType: GenerationModelType.Mmdit26AModel }),
  ),
]

/**
 * @param mapItem [width, height] or [width, height, extraProps]
 * @param extraInfo
 * @returns
 */
function sizeMapFn(
  mapItem: Size | [number, number, Record<string, any>],
  extraInfo: {
    memberOnly?: boolean
    modelType: GenerationModelType
  },
): SizeInfo {
  const [width, height, extraProps] = mapItem
  return {
    width,
    height,
    extraProps: {
      memberOnly: extraInfo.memberOnly,
      modelType: extraInfo.modelType,
      /** Ratio */
      label: getRatioOfTwoNumbers(width, height, {
        replacements: RATIO_LABEL_REPLACEMENTS,
      }),
      ...extraProps,
    },
  }
}

export const defaultTaskFormValues = {
  width: 512,
  height: 768,
  batchSize: 4,
  negativePrompts:
    'nsfw, lowres, bad anatomy, bad hands, text, error, missing fingers, extra digit, fewer digits, cropped, worst quality, low quality, normal quality, jpeg artifacts, signature, watermark, username, blurry',
  samplingSteps: 28,
  samplingMethod: 'Euler a',
  cfgScale: 11,

  // remove this val if not performing i2i
  strength: 0.55,

  // remove this val if not performing enlarge
  enlargeModel: 'R-ESRGAN 4x+ Anime6B',
}

/**
 * Default sampling method from server when input is not provided
 */
export const DEFAULT_SAMPLING_METHOD_IF_EMPTY = 'DPM++ 2M Karras'

/**
 * Default priority (1000) was previously "high"
 * Low priority (0) was previously "default"
 */
export const TASK_PRIORITIES = {
  default: 1000,
  /**
   * "Turbo mode" is a member-exclusive feature.
   * Higher priority than "low" but costs the same as "low".
   */
  turboMode: 500,
  low: 0,
}

export const DEFAULT_CLIPSKIP_VAL_FOR_SDV1 = 2
/**
 * Cosmetic only;
 * This value has no effect since backend always gen with 2
 */
export const DEFAULT_CLIPSKIP_VAL_FOR_NON_SDV1 = 2

export type EnhancementPlugin = ValueOf<
  NonNullable<DynamicConstants['imageEnhancementPlugins']>
>

export const DEFAULT_SAMPLING_STEPS = 20

export const DEFAULT_LORA_MODEL_WEIGHT = 0.7
